import {getCloudinaryCdnHostName} from './cloudinary-utils';
import {stringHasValue} from './generic-utils';
import config from '../config';
import {CLOUDINARY_SYNC_VIDEO_LIMIT_MB} from '../constants';
import {
  IMAGE_CONFIG,
  VIDEO_CONFIG,
  DIRTY_VIDEO_EXTENSIONS,
  PDF_CONFIG,
  EAGER_CACHE,
  OPTIMIZED_VIDEO_DELIVERY_OPTIONS
} from '../media-config';
import map from 'lodash/map';
import forEach from 'lodash/forEach';

export const svgIconFileLimit = 95 * 1024;

export const getFileTypes = config => {
  return config.types.map(extension => `${config.type}/${extension}`);
};

const videoTypes = getFileTypes(VIDEO_CONFIG);
export const imageTypes = getFileTypes(IMAGE_CONFIG);
export const acceptedFileTypes = [...videoTypes, ...imageTypes];
export const pdfTypes = getFileTypes(PDF_CONFIG);

export const getResourceType = file => {
  const [type] = (file.type || '').split('/');

  // If type is not provided, only check for .mkv because there is no type provided for it
  if (!type || type === '') {
    const fileName = file.name || '';
    const extension = fileName.split('.').pop().toLowerCase();

    if (extension === 'mkv') {
      return 'video';
    }
  }

  return type;
};

export const isAllowedResourceType = type => type === 'video' || type === 'image';

export const isAllowedPDFType = type => type === 'pdf' || type === 'application';

export const convertBToMb = b => b / 1000000;

const CLIENT_FALLBACK_VIDEO_UPLOAD_LIMIT_MB = 20;

export const getAllowedSizeForResource = (resourceType: string, videoLimitMb?: number) => {
  switch (resourceType) {
    case 'video':
      return videoLimitMb ?? CLIENT_FALLBACK_VIDEO_UPLOAD_LIMIT_MB;
    case 'image':
      return IMAGE_CONFIG.mblimit;
    case 'pdf':
      return PDF_CONFIG.mbLimit;
    case 'application':
      return PDF_CONFIG.mbLimit;
    default:
      throw new Error(`Resource type ${resourceType} is not supported`);
  }
};

export const isAllowedSize = (file, resourceType: string, videoLimitMb?: number) => {
  const fileLimitInMb = getAllowedSizeForResource(resourceType, videoLimitMb);
  const {size = 0} = file;
  const fileSizeInMb = convertBToMb(size);
  return fileSizeInMb < fileLimitInMb;
};

export const getUploadPresetForFile = (file: File, {optimizedVideos = false} = {}) => {
  const resourceType = getResourceType(file);
  if (resourceType !== 'video') {
    return IMAGE_CONFIG.uploadPreset;
  }
  return optimizedVideos && convertBToMb(file.size) <= CLOUDINARY_SYNC_VIDEO_LIMIT_MB
    ? VIDEO_CONFIG.optimizedUploadPreset
    : VIDEO_CONFIG.uploadPreset;
};

export const isSupportedFileType = (file, resourceType, allowedFileTypes?) => {
  const supportedFileTypes = allowedFileTypes || (resourceType === 'video' ? videoTypes : imageTypes);

  // Check type first
  if (file.type && supportedFileTypes.indexOf(file.type) !== -1) {
    return true;
  }
  const fileName = file.name || '';
  const extension = fileName.split('.').pop().toLowerCase();
  if (extension === 'mkv' && resourceType === 'video') {
    return true;
  }
  return false;
};

export const getExtensionFromUrl = url => {
  url = url || '';
  if (url) {
    const splitted = url.split('.');
    return splitted[splitted.length - 1];
  }
  return '';
};

export const getFixedExtensionFromUrl = url => {
  const extension = getExtensionFromUrl(url);
  if (DIRTY_VIDEO_EXTENSIONS.indexOf(extension) !== -1) {
    return 'mp4';
  }
  return extension;
};

export const isVideoUrl = (url = '') => Boolean(url) && url.indexOf('/video/upload/') !== -1;
export const isImageUrl = (url = '') => Boolean(url) && IMAGE_CONFIG.types.indexOf(getExtensionFromUrl(url)) !== -1;
export const isVideoType = (type = '') => Boolean(type) && videoTypes.indexOf(type) !== -1;
export const isImageType = (type = '') => Boolean(type) && imageTypes.indexOf(type) !== -1;

export const getThumbnail = (url, returnPlaceholder = true, options: string[] = [], fullVideo = false) => {
  url = url || '';

  if (!stringHasValue(url)) {
    return returnPlaceholder ? config.placeholderImageUrl : null;
  }

  if (isVideoUrl(url)) {
    return getCloudinaryVideoUrl(url, fullVideo ? getExtensionFromUrl(url) : 'jpg', options);
  }

  return getCloudinaryImageUrl(url, options);
};

interface ResourceAllowedOptions {
  allowVideos: boolean;
  allowedFileTypes: string[] | undefined;
  videoLimitMb: number | undefined;
}

export const resourceAllowed = (file, {allowVideos, allowedFileTypes, videoLimitMb}: ResourceAllowedOptions) => {
  const resourceType = getResourceType(file);
  const allowedSize = getAllowedSizeForResource(resourceType, videoLimitMb);

  if (resourceType === 'video' && allowVideos === false) {
    return {
      allowed: false,
      error: {
        type: 'videosForbidden'
      }
    };
  }

  if (!isAllowedResourceType(resourceType)) {
    return {
      allowed: false,
      error: {
        type: 'typeForbidden'
      }
    };
  }

  if (!isSupportedFileType(file, resourceType, allowedFileTypes)) {
    return {
      allowed: false,
      error: {
        type: 'extensionForbidden'
      }
    };
  }

  if (!isAllowedSize(file, resourceType, videoLimitMb)) {
    return {
      allowed: false,
      error: {
        type: 'tooLarge',
        data: {allowedSize}
      }
    };
  }

  return {allowed: true};
};

export const resourcePDFAllowed = (
  file
): {allowed: boolean; error?: {type: 'typeForbidden' | 'tooLarge'; data?: {allowedSize: number}}} => {
  const resourceType = getResourceType(file);
  const allowedSize = getAllowedSizeForResource(resourceType);

  if (!isAllowedPDFType(resourceType)) {
    return {
      allowed: false,
      error: {
        type: 'typeForbidden'
      }
    };
  }

  if (!isAllowedSize(file, resourceType)) {
    return {
      allowed: false,
      error: {
        type: 'tooLarge',
        data: {allowedSize}
      }
    };
  }

  return {allowed: true};
};

export const stepNoteResourceAllowed = file => {
  const LIMIT_SIZE = svgIconFileLimit;

  if (!file.type.includes('image/svg')) {
    return {
      allowed: false,
      error: {
        type: 'extensionForbidden'
      }
    };
  }

  if (LIMIT_SIZE < file.size) {
    return {
      allowed: false,
      error: {
        type: 'tooLarge',
        data: {allowedSize: LIMIT_SIZE}
      }
    };
  }

  return {allowed: true};
};

export const getFilenameAndTransformationsFromCloudinaryUrl = url => {
  const regexp = /upload\/((?:(?:[^/]+,)?(?:c_crop|a_|ar_)[^/]*\/){0,4})(.+\.[^.]+)$/;
  const match = url.match(regexp);
  if (!match) {
    return {};
  }
  const [, transformations, filename] = match;
  return {transformations, filename};
};

const getCloudFromCloudinaryUrl = (url = '') => {
  const match = url.match(/.com\/(.*?)\//);
  return match && match[1];
};

export const getIdFromCloudinaryVideoUrl = url => {
  const splitted = url.split('/');
  const indexOfUpload = splitted.indexOf('upload');
  const urlElementsAfterUploadAndVersion = splitted.slice(indexOfUpload + 2, splitted.length);

  const nameAndExtension = urlElementsAfterUploadAndVersion.join('/');
  const split = nameAndExtension.split('.');
  return split[0];
};

const getOptionsAsString = options => (options.length > 0 ? `${options.join(',')}/` : '');

export const generateOptionString = option => {
  if (!option) {
    return [];
  }

  const shortParams = {
    angle: 'a',
    aspect_ratio: 'ar',
    background: 'b',
    border: 'bo',
    crop: 'c',
    color: 'co',
    dpr: 'dpr',
    effect: 'e',
    flags: 'l',
    height: 'h',
    keyframe_interval: 'ki',
    overlay: 'l',
    opacity: 'o',
    quality: 'q',
    radius: 'r',
    named_transformation: 't',
    underlay: 'u',
    width: 'w',
    x: 'x',
    y: 'y',
    zoom: 'z',
    audio_codec: 'ac',
    audio_frequency: 'af',
    bit_rate: 'br',
    color_space: 'cs',
    default_image: 'd',
    delay: 'dl',
    density: 'dn',
    duration: 'du',
    end_offset: 'eo',
    fetch_format: 'f',
    gravity: 'g',
    page: 'pg',
    prefix: 'p',
    start_offset: 'so',
    streaming_profile: 'sp',
    video_codec: 'vc',
    video_sampling: 'vs'
  };

  return map(option, (value, key) => {
    if (shortParams[key]) return shortParams[key] + '_' + value;
    else return key + '_' + value;
  });
};

export const getCloudinaryImageUrl = (url, options: string[] = []) => {
  if (!url) {
    return null;
  }
  const {transformations, filename} = getFilenameAndTransformationsFromCloudinaryUrl(url);
  const cloud = getCloudFromCloudinaryUrl(url);
  const localisedCloudinaryCdnHostName = getCloudinaryCdnHostName();
  const optionsString = getOptionsAsString(options);
  const path = `/${cloud}/image/upload/${transformations}${optionsString}${filename}`;
  const cloudinaryImageUrl = `https://${localisedCloudinaryCdnHostName}${path}`;
  return cloudinaryImageUrl;
};

export const getCloudinaryVideoUrl = (url, fileType, options = ['ac_none']) => {
  if (!url) {
    return null;
  }

  if (DIRTY_VIDEO_EXTENSIONS.includes(fileType)) {
    fileType = 'mp4';
  }

  const id = getIdFromCloudinaryVideoUrl(url);
  const cloud = getCloudFromCloudinaryUrl(url);
  const localisedCloudinaryCdnHostName = getCloudinaryCdnHostName();
  const optionsString = getOptionsAsString(options);
  const path = `/${cloud}/video/upload/${optionsString}${id}.${fileType}`;

  return `https://${localisedCloudinaryCdnHostName}${path}`;
};

export const getVideoPlaybackUrl = (url: string, {enableAudio = false, optimizedVideos = false} = {}) => {
  if (optimizedVideos) {
    const options = enableAudio ? OPTIMIZED_VIDEO_DELIVERY_OPTIONS : [...OPTIMIZED_VIDEO_DELIVERY_OPTIONS, 'ac_none'];
    return getCloudinaryVideoUrl(url, 'mp4', options);
  }

  const eagerCacheConfig: {quality: number; audio_codec?: string} = {...EAGER_CACHE.video[0]};
  if (enableAudio) {
    delete eagerCacheConfig.audio_codec;
  }
  return getCloudinaryVideoUrl(url, getExtensionFromUrl(url), generateOptionString(eagerCacheConfig));
};

export const getInnerWidth = () => {
  return window.innerWidth;
};

export const isGif = url => Boolean(url) && getExtensionFromUrl(url) === 'gif';

const removeSvgProperties = ['fill'];
const removeSvgStyles = ['fill'];
export const removeSvgColors = svgContent => {
  const parser = new DOMParser();
  const parsedHtml = parser.parseFromString(svgContent, 'text/html');

  //remove attributes
  forEach(removeSvgProperties, namespace => {
    const elements = parsedHtml.getElementsByTagName('*');
    forEach(elements, elem => {
      forEach(elem.attributes, (value, attr) => {
        if (value.nodeName.match(namespace)) {
          elem.removeAttribute(value.nodeName);
        }
      });
    });
  });

  //remove style properties
  forEach(removeSvgStyles, namespace => {
    const elements = parsedHtml.getElementsByTagName('*');
    forEach(elements, elem => {
      if (elem.hasAttribute('style')) {
        let style = elem.getAttribute('style') as string;
        const styleArr = style.split(';');
        const res = styleArr.filter(value => {
          return !value.includes(namespace);
        });
        style = res.join(';');

        elem.setAttribute('style', style);
      }
    });
  });

  return parsedHtml.getElementsByTagName('svg')[0].outerHTML;
};
